/**
 * Tracking Helper Functions for Prozpector Analytics
 *
 * Pre-built functions for tracking user events throughout the app.
 * Just import and call these functions where needed.
 */

/// <reference path="../types/global.d.ts" />
import { supabase } from './supabase';

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// UTM parameters type
interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

// Extract UTM parameters from URL
const getUTMParams = (): UTMParams => {
  const params = new URLSearchParams(window.location.search);
  const utmParams: UTMParams = {};

  // Extract UTM parameters
  const utmKeys: (keyof UTMParams)[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  // Store UTM params in sessionStorage for attribution throughout the session
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  }

  // Return stored UTM params if they exist (for attribution persistence)
  const storedUTM = sessionStorage.getItem('utm_params');
  return storedUTM ? JSON.parse(storedUTM) : utmParams;
};

// Parse user agent to extract device info
const getDeviceInfo = (): {
  device_type: string;
  browser: string;
  os: string;
} => {
  const ua = navigator.userAgent;

  // Detect device type
  let device_type = 'desktop';
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    device_type = 'tablet';
  } else if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)
  ) {
    device_type = 'mobile';
  }

  // Detect browser
  let browser = 'Unknown';
  if (ua.indexOf('Firefox') > -1) {
    browser = 'Firefox';
  } else if (ua.indexOf('SamsungBrowser') > -1) {
    browser = 'Samsung Browser';
  } else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) {
    browser = 'Opera';
  } else if (ua.indexOf('Trident') > -1) {
    browser = 'Internet Explorer';
  } else if (ua.indexOf('Edge') > -1 || ua.indexOf('Edg') > -1) {
    browser = 'Edge';
  } else if (ua.indexOf('Chrome') > -1) {
    browser = 'Chrome';
  } else if (ua.indexOf('Safari') > -1) {
    browser = 'Safari';
  }

  // Detect OS
  let os = 'Unknown';
  if (ua.indexOf('Win') > -1) {
    os = 'Windows';
  } else if (ua.indexOf('Mac') > -1) {
    os = 'macOS';
  } else if (ua.indexOf('X11') > -1 || ua.indexOf('Linux') > -1) {
    os = 'Linux';
  } else if (ua.indexOf('Android') > -1) {
    os = 'Android';
  } else if (ua.indexOf('like Mac') > -1) {
    os = 'iOS';
  }

  return { device_type, browser, os };
};

// Detect if the user agent is a known bot
const isBot = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();

  // Known bot patterns
  const botPatterns = [
    // Search engine bots
    'googlebot', 'bingbot', 'yandexbot', 'baiduspider', 'duckduckbot',
    'slurp', 'sogou', 'exabot', 'facebot', 'facebookexternalhit',
    // Crawlers & scrapers
    'crawler', 'spider', 'scraper', 'bot/', 'bot;',
    // Headless browsers
    'headlesschrome', 'phantomjs', 'puppeteer', 'selenium', 'webdriver',
    // HTTP libraries
    'python-requests', 'python-urllib', 'curl/', 'wget/', 'httpie/',
    'axios/', 'node-fetch', 'go-http-client', 'java/', 'libwww', 'php/',
    // Monitoring bots
    'uptimerobot', 'pingdom', 'site24x7', 'statuscake', 'newrelic', 'datadog',
    // SEO tools
    'ahrefs', 'semrush', 'moz.com', 'screaming frog', 'majestic',
    // Social crawlers
    'twitterbot', 'linkedinbot', 'slackbot', 'telegrambot', 'whatsapp', 'discordbot',
    // Other
    'applebot', 'petalbot', 'bytespider', 'gptbot', 'claudebot', 'anthropic', 'ccbot'
  ];

  return botPatterns.some(pattern => ua.includes(pattern));
};

// Fetch geographic location from IP (cached for session)
const getGeographicData = async (): Promise<{
  country?: string;
  country_code?: string;
  region?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
} | null> => {
  // Check if we already have geo data cached in sessionStorage
  const cachedGeo = sessionStorage.getItem('geo_data');
  if (cachedGeo) {
    try {
      return JSON.parse(cachedGeo);
    } catch {
      // Invalid cache, proceed to fetch
    }
  }

  try {
    // Use ipapi.co (free tier with HTTPS support, 1000 req/day)
    const response = await fetch('https://ipapi.co/json/');

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (!data.error) {
      const geoData = {
        country: data.country_name,
        country_code: data.country_code,
        region: data.region,
        city: data.city,
        latitude: data.latitude,
        longitude: data.longitude,
      };

      // Cache for the session
      sessionStorage.setItem('geo_data', JSON.stringify(geoData));

      return geoData;
    }

    return null;
  } catch (error) {
    // Fail silently - geo data is nice to have but not critical
    if (import.meta.env.DEV) {
      console.warn('Failed to fetch geographic data:', error);
    }
    return null;
  }
};

// Get or set first-touch attribution (acquisition source)
// This persists in localStorage across sessions to track initial acquisition
const getFirstTouchAttribution = (currentUtm: UTMParams): {
  first_touch_utm_source?: string;
  first_touch_utm_medium?: string;
  first_touch_utm_campaign?: string;
  first_touch_utm_content?: string;
  first_touch_utm_term?: string;
  first_touch_timestamp?: string;
} => {
  const STORAGE_KEY = 'first_touch_attribution';

  // Check if we already have first-touch attribution stored
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // Invalid data, continue to set new
    }
  }

  // No first-touch stored - use current UTM as first touch
  if (currentUtm.utm_source || currentUtm.utm_campaign) {
    const firstTouch = {
      first_touch_utm_source: currentUtm.utm_source,
      first_touch_utm_medium: currentUtm.utm_medium,
      first_touch_utm_campaign: currentUtm.utm_campaign,
      first_touch_utm_content: currentUtm.utm_content,
      first_touch_utm_term: currentUtm.utm_term,
      first_touch_timestamp: new Date().toISOString(),
    };

    // Store permanently in localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(firstTouch));
    return firstTouch;
  }

  // No current UTM either
  return {};
};

// Get last-touch attribution (conversion source)
// This updates on significant events (signup, trial, conversion)
const getLastTouchAttribution = (
  eventName: string,
  currentUtm: UTMParams
): {
  last_touch_utm_source?: string;
  last_touch_utm_medium?: string;
  last_touch_utm_campaign?: string;
  last_touch_utm_content?: string;
  last_touch_utm_term?: string;
  last_touch_timestamp?: string;
} => {
  // Significant events that should update last-touch attribution
  const SIGNIFICANT_EVENTS = [
    'signup',
    'trial_started',
    'subscription_created',
    'conversion',
    'converted',
    'payment_method_added'
  ];

  // Only update last-touch on significant events
  if (!SIGNIFICANT_EVENTS.includes(eventName)) {
    // Return stored last-touch if available
    const stored = sessionStorage.getItem('last_touch_attribution');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return {};
      }
    }
    return {};
  }

  // This is a significant event - update last-touch
  if (currentUtm.utm_source || currentUtm.utm_campaign) {
    const lastTouch = {
      last_touch_utm_source: currentUtm.utm_source,
      last_touch_utm_medium: currentUtm.utm_medium,
      last_touch_utm_campaign: currentUtm.utm_campaign,
      last_touch_utm_content: currentUtm.utm_content,
      last_touch_utm_term: currentUtm.utm_term,
      last_touch_timestamp: new Date().toISOString(),
    };

    // Store in session (will update throughout session)
    sessionStorage.setItem('last_touch_attribution', JSON.stringify(lastTouch));
    return lastTouch;
  }

  return {};
};

// Core tracking function
async function trackEvent(
  eventName: string,
  category: string = 'general',
  properties: Record<string, any> = {}
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // Get UTM parameters and device info
    const utmParams = getUTMParams();
    const deviceInfo = getDeviceInfo();

    // Get geographic data (async, cached)
    const geoData = await getGeographicData();

    // Get attribution data (first-touch and last-touch)
    const firstTouchAttribution = getFirstTouchAttribution(utmParams);
    const lastTouchAttribution = getLastTouchAttribution(eventName, utmParams);

    const event = {
      user_id: user?.id || null,
      event_name: eventName,
      event_category: category,
      properties,
      session_id: getSessionId(),
      page_url: window.location.href,
      page_title: document.title || 'Untitled',
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),

      // Current UTM tracking (what brought them THIS visit)
      ...utmParams,

      // First-touch attribution (how we acquired them)
      ...firstTouchAttribution,

      // Last-touch attribution (what drove conversion)
      ...lastTouchAttribution,

      // Device information
      ...deviceInfo,

      // Geographic data (if available)
      ...(geoData || {}),
    };

    // Insert event into database
    const { error } = await supabase
      .from('analytics_events')
      .insert(event);

    if (error) {
      console.error('Analytics tracking error:', error);
    }

    // Log in development
    if (import.meta.env.DEV) {
      console.log('📊 Event tracked:', eventName, {
        ...properties,
        utm: utmParams,
        firstTouch: firstTouchAttribution,
        lastTouch: lastTouchAttribution,
        device: deviceInfo,
        geo: geoData
      });
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
}

// ============================================
// AUTH EVENTS
// ============================================

export const trackSignup = (email: string, plan: string = 'free') => {
  trackEvent('signup', 'auth', { email, plan });
};

export const trackTrialStarted = (trialDays: number = 14) => {
  trackEvent('trial_started', 'auth', { trial_days: trialDays });
};

export const trackLogin = () => {
  trackEvent('login', 'auth');
};

export const trackLogout = () => {
  trackEvent('logout', 'auth');
};

export const trackPasswordReset = (email: string) => {
  trackEvent('password_reset_requested', 'auth', { email });
};

// ============================================
// PROSPECT EVENTS
// ============================================

export const trackProspectCreated = (prospectId?: string, source?: string) => {
  trackEvent('prospect_created', 'prospects', { prospect_id: prospectId, source });
};

export const trackProspectUpdated = (prospectId: string, fields?: string[]) => {
  trackEvent('prospect_updated', 'prospects', { prospect_id: prospectId, fields });
};

export const trackProspectDeleted = (prospectId: string) => {
  trackEvent('prospect_deleted', 'prospects', { prospect_id: prospectId });
};

export const trackProspectStageChanged = (prospectId: string, fromStage: string, toStage: string) => {
  trackEvent('prospect_stage_changed', 'prospects', {
    prospect_id: prospectId,
    from_stage: fromStage,
    to_stage: toStage
  });
};

export const trackProspectImported = (count: number, source: string = 'csv') => {
  trackEvent('prospects_imported', 'prospects', { count, source });
};

// ============================================
// SEQUENCE EVENTS
// ============================================

export const trackSequenceCreated = (sequenceId?: string, name?: string) => {
  trackEvent('sequence_created', 'sequences', { sequence_id: sequenceId, name });
};

export const trackSequenceStarted = (sequenceId: string, prospectCount?: number) => {
  trackEvent('sequence_started', 'sequences', { sequence_id: sequenceId, prospect_count: prospectCount });
};

export const trackSequencePaused = (sequenceId: string) => {
  trackEvent('sequence_paused', 'sequences', { sequence_id: sequenceId });
};

export const trackSequenceCompleted = (sequenceId: string) => {
  trackEvent('sequence_completed', 'sequences', { sequence_id: sequenceId });
};

export const trackEmailSent = (sequenceId?: string, stepNumber?: number) => {
  trackEvent('email_sent', 'sequences', { sequence_id: sequenceId, step_number: stepNumber });
};

export const trackEmailOpened = (sequenceId?: string, emailId?: string) => {
  trackEvent('email_opened', 'sequences', { sequence_id: sequenceId, email_id: emailId });
};

export const trackEmailClicked = (sequenceId?: string, emailId?: string) => {
  trackEvent('email_clicked', 'sequences', { sequence_id: sequenceId, email_id: emailId });
};

export const trackEmailReplied = (sequenceId?: string, emailId?: string) => {
  trackEvent('email_replied', 'sequences', { sequence_id: sequenceId, email_id: emailId });
};

// ============================================
// TEMPLATE EVENTS
// ============================================

export const trackTemplateCreated = (templateId?: string, name?: string) => {
  trackEvent('template_created', 'templates', { template_id: templateId, name });
};

export const trackTemplateUpdated = (templateId: string) => {
  trackEvent('template_updated', 'templates', { template_id: templateId });
};

export const trackTemplateDeleted = (templateId: string) => {
  trackEvent('template_deleted', 'templates', { template_id: templateId });
};

export const trackTemplateUsed = (templateId: string) => {
  trackEvent('template_used', 'templates', { template_id: templateId });
};

// ============================================
// INTEGRATION EVENTS
// ============================================

export const trackIntegrationConnected = (integrationName: string) => {
  trackEvent('integration_connected', 'integrations', { integration: integrationName });
};

export const trackIntegrationDisconnected = (integrationName: string) => {
  trackEvent('integration_disconnected', 'integrations', { integration: integrationName });
};

export const trackIntegrationError = (integrationName: string, error: string) => {
  trackEvent('integration_error', 'integrations', { integration: integrationName, error });
};

// ============================================
// SUBSCRIPTION EVENTS
// ============================================

export const trackSubscriptionCreated = (plan: string, amount?: number) => {
  trackEvent('subscription_created', 'subscription', { plan, amount });
};

export const trackSubscriptionCanceled = (plan?: string, reason?: string) => {
  trackEvent('subscription_canceled', 'subscription', { plan, reason });
};

export const trackConversion = (plan: string, fromTrial: boolean = true) => {
  trackEvent('conversion', 'subscription', { plan, from_trial: fromTrial });
};

export const trackPaymentMethodAdded = () => {
  trackEvent('payment_method_added', 'subscription');
};

export const trackPaymentFailed = (reason?: string) => {
  trackEvent('payment_failed', 'subscription', { reason });
};

// ============================================
// ONBOARDING EVENTS
// ============================================

export const trackOnboardingStarted = () => {
  trackEvent('onboarding_started', 'onboarding');
};

export const trackOnboardingStepCompleted = (stepName: string, stepNumber: number) => {
  trackEvent('onboarding_step_completed', 'onboarding', { step_name: stepName, step_number: stepNumber });
};

export const trackOnboardingCompleted = () => {
  trackEvent('onboarding_completed', 'onboarding');
};

export const trackOnboardingSkipped = (atStep?: number) => {
  trackEvent('onboarding_skipped', 'onboarding', { at_step: atStep });
};

// ============================================
// SETTINGS EVENTS
// ============================================

export const trackSettingsChanged = (setting: string, value?: any) => {
  trackEvent('settings_changed', 'settings', { setting, value });
};

export const trackDataExported = (dataType: string) => {
  trackEvent('data_exported', 'settings', { data_type: dataType });
};

export const trackAccountDeleted = () => {
  trackEvent('account_deleted', 'settings');
};

export const trackMFAEnabled = () => {
  trackEvent('mfa_enabled', 'settings');
};

export const trackMFADisabled = () => {
  trackEvent('mfa_disabled', 'settings');
};

// ============================================
// FEATURE USAGE EVENTS
// ============================================

export const trackFeatureUsed = (featureName: string, properties?: Record<string, any>) => {
  trackEvent('feature_used', 'features', { feature: featureName, ...properties });
};

export const trackSearch = (query: string, resultsCount?: number) => {
  trackEvent('search', 'features', { query, results_count: resultsCount });
};

export const trackExport = (exportType: string, count?: number) => {
  trackEvent('export', 'features', { export_type: exportType, count });
};

// ============================================
// PAGE VIEW TRACKING
// ============================================

export const trackPageView = (pageName: string, path?: string) => {
  trackEvent('page_view', 'navigation', { page_name: pageName, path: path || window.location.pathname });
};

// ============================================
// ERROR TRACKING
// ============================================

export const trackError = (errorMessage: string, errorStack?: string, context?: string) => {
  trackEvent('error', 'errors', {
    message: errorMessage,
    stack: errorStack,
    context
  });
};

// ============================================
// BLOG TRACKING
// ============================================

export const trackBlogPostView = (slug: string, title: string, category?: string) => {
  trackEvent('blog_post_view', 'content', {
    blog_slug: slug,
    blog_title: title,
    blog_category: category
  });
};

export const trackBlogCTAClick = (slug: string, ctaType: 'article' | 'bottom' | 'signup' | 'pricing', ctaText?: string) => {
  trackEvent('blog_cta_click', 'content', {
    blog_slug: slug,
    cta_type: ctaType,
    cta_text: ctaText
  });
};

export const trackBlogTimeOnPage = (slug: string, timeSeconds: number) => {
  trackEvent('blog_time_on_page', 'content', {
    blog_slug: slug,
    time_seconds: timeSeconds,
    time_minutes: Math.round(timeSeconds / 60)
  });
};

export const trackBlogRelatedClick = (fromSlug: string, toSlug: string) => {
  trackEvent('blog_related_click', 'content', {
    from_slug: fromSlug,
    to_slug: toSlug
  });
};

export const trackBlogCategoryFilter = (category: string) => {
  trackEvent('blog_category_filter', 'content', {
    category
  });
};

export const trackBlogSearch = (query: string) => {
  trackEvent('blog_search', 'content', {
    search_query: query
  });
};

// ============================================
// SCROLL DEPTH TRACKING
// ============================================

export const trackScrollDepth = (event: {
  milestone: 25 | 50 | 75 | 100;
  pageUrl: string;
  pageTitle: string;
  timeToReach: number;
}) => {
  trackEvent('scroll_depth', 'engagement', {
    scroll_depth_percent: event.milestone,
    page_url: event.pageUrl,
    page_title: event.pageTitle,
    time_to_reach_ms: event.timeToReach,
    time_to_reach_seconds: Math.round(event.timeToReach / 1000),
  });
};

// Expose trackScrollDepth globally for the useScrollDepth hook
if (typeof window !== 'undefined') {
  window.trackScrollDepth = trackScrollDepth;
}

// ============================================
// LINK CLICK TRACKING
// ============================================

export const trackLinkClick = (event: {
  linkUrl: string;
  linkText: string;
  linkType: 'internal' | 'external';
  sourcePageUrl: string;
  sourcePageTitle: string;
}) => {
  trackEvent('link_click', 'engagement', {
    link_url: event.linkUrl,
    link_text: event.linkText,
    link_type: event.linkType,
    source_page_url: event.sourcePageUrl,
    source_page_title: event.sourcePageTitle,
  });
};

// Expose trackLinkClick globally for the useLinkTracking hook
if (typeof window !== 'undefined') {
  window.trackLinkClick = trackLinkClick;
}
